/**
 * Contrato dos recebíveis, liquidações e cobrança (spec 03 §5 · tela 4.4 · passo 4.5).
 *
 * O `saldo` que sai daqui é **cobertura − amortizado**, não `valor − Σ liquidações`:
 * juros e multa entram na conta bancária e NÃO abatem a parcela, tarifa não entrou e
 * abate (D-91). A direção vive em `fn_amortizado`, no banco, num lugar só.
 */
import { z } from 'zod'
import { chaveDeComando, codigoDeMoeda, dataNominal, uuid } from './comum.js'

export const faixaDeAging = z.enum(['a_vencer', 'd1_30', 'd31_60', 'd61_90', 'd90_mais'])
export type FaixaDeAging = z.infer<typeof faixaDeAging>

export const statusDoRecebivel = z.enum(['pendente', 'nf_emitida', 'recebido', 'cancelado'])
export type StatusDoRecebivel = z.infer<typeof statusDoRecebivel>

/**
 * A direção sai do TIPO, e não de um campo de sinal (D-91).
 * A menor: conta para fechar a parcela. A maior: veio além, e não infla.
 */
export const tipoDeDiferenca = z.enum([
  'desconto', 'tarifa', 'retencao', 'cambial', 'juros', 'multa',
])
export type TipoDeDiferenca = z.infer<typeof tipoDeDiferenca>

export const origemDaDiferenca = z.enum(['deducao_nf', 'adicional'])
export const entradaDeConferenciaDeDesconto = z.object({
  origem: origemDaDiferenca,
  motivo: z.string().trim().min(1).max(1000),
})
export type EntradaDeConferenciaDeDesconto = z.infer<typeof entradaDeConferenciaDeDesconto>

export const canalDeCobranca = z.enum([
  'telefone', 'whatsapp', 'email', 'reuniao', 'presencial', 'carta', 'outro',
])

/**
 * Uma BAIXA já registrada na parcela — o que o estorno desfaz.
 *
 * A API tinha `POST .../liquidacoes` e `DELETE /liquidacoes/:id`, e nenhuma leitura: dava
 * para criar e apagar uma baixa, e não para saber quais existiam. A tela não conseguia
 * oferecer "Estornar" porque não tinha o id — o ato existia e era inalcançável.
 *
 * A DIFERENÇA vem junta e por extenso (D-91). Ela é o que explica por que 9.980 fecharam
 * uma parcela de 10.000, e uma diferença que só existe no banco é uma diferença que
 * ninguém contesta com o cliente.
 */
export const baixaDoRecebivel = z.object({
  id: uuid,
  data: dataNominal,
  valor: z.string(),
  forma: z.enum(['pix', 'ted', 'boleto', 'dinheiro', 'outro']).nullable(),
  contaBancariaNome: z.string().nullable(),
  diferencaTipo: z.string().nullable(),
  diferencaValor: z.string().nullable(),
  diferencaMotivo: z.string().nullable(),
  diferencaOrigem: origemDaDiferenca.nullable(),
  /** Quem registrou. `null` quando a baixa veio da conciliação (6.4), e não de gente. */
  registradaPor: z.string().nullable(),
  /** Veio da conciliação bancária — a baixa tem lastro no extrato. */
  daConciliacao: z.boolean(),
  capacidades: z.object({ podeEstornar: z.boolean() }),
})
export type BaixaDoRecebivel = z.infer<typeof baixaDoRecebivel>

export const recebivel = z.object({
  id: uuid,
  pedidoId: uuid,
  pedidoCodigo: z.string(),
  clienteNome: z.string(),
  clienteId: uuid.nullable(),
  setor: z.string().nullable(),
  itemContabilId: uuid.nullable(),
  itemContabilNome: z.string().nullable(),
  numeroParcela: z.number().int(),
  totalParcelas: z.number().int(),
  /**
   * **Quando esta parcela deve ser FATURADA** (0131) — não quando se espera receber.
   *
   * As duas datas responderam a mesma pergunta por tempo demais: quem olhava a 4.4 via
   * só o vencimento e tinha de fazer a conta do prazo de cabeça para saber quando a nota
   * precisava sair. Agora as duas aparecem lado a lado, e cada coluna responde a sua.
   *
   * `null` em parcela histórica anterior à programação e em ciclo ainda não programado:
   * a tela DIZ que não há, não inventa data (DS-99/DS-211).
   */
  faturarEm: dataNominal.nullable(),
  vencimento: dataNominal,
  valor: z.string().nullable(),
  moeda: codigoDeMoeda,
  status: statusDoRecebivel,
  /** Cobertura por nota viva (D-90: nota + deduções destacadas). */
  coberto: z.string().nullable(),
  /** O que a parcela já abateu, com a diferença classificada (D-91). */
  amortizado: z.string().nullable(),
  saldo: z.string().nullable(),
  /** DS496: líquido AUTORIZADO, sem cobrar dedução fiscal nem descontá-la duas vezes. NULL também pode exigir conferência de origem. */
  saldoEmAberto: z.string().nullable(),
  requerConferencia: z.boolean(),
  /** Sem saldo autorizado em aberto, não há faixa de cobrança. */
  aging: faixaDeAging.nullable(),
  /**
   * O dia em que se espera RECEBER esta parcela — e ele deixou de ser uma projeção.
   *
   * Era a camada Derivado redistribuída por parcela: projeção do pedido mais o
   * deslocamento de cada uma. Desde o modelo por parcela é o próprio
   * `recebivel.vencimento`, combinado com o cliente e imutável (DS-11). O campo
   * continua existindo porque a tela o usa como legenda da parcela ainda sem nota;
   * o que mudou é que ele passou a ser fato em vez de inferência.
   */
  vencimentoPrevisto: dataNominal.nullable(),
  diasDeAtraso: z.number().int(),
  reguaSuspensa: z.boolean(),
  reguaSuspensaMotivo: z.string().nullable(),
  perdaEstimadaPct: z.string().nullable(),
  renegociacaoId: uuid.nullable(),
  /*
    A NOTA VIVA da parcela — e por que são três campos e não um.

    Um recebível pode ser coberto por mais de uma nota (uma parcial e a complementar,
    uma substituição). `nfId` e `numeroNf` só vêm preenchidos quando há EXATAMENTE uma:
    com duas, escolher "a primeira" faria a coluna NF afirmar uma cobertura que não é a
    inteira, e o deep-link `4.5?nf=` levaria a uma das duas sem dizer que havia outra.
    `notasVivas` é a contagem, e é ela que permite à tela escrever "2 notas" em vez de
    mentir escolhendo uma.
  */
  nfId: uuid.nullable(),
  numeroNf: z.string().nullable(),
  notasVivas: z.number().int(),
})
export type Recebivel = z.infer<typeof recebivel>

/** Uma troca explícita sempre escolhe um item válido; limpar a sugestão não faz parte da v1. */
export const entradaDeClassificacaoDoRecebivel = z.object({ itemContabilId: uuid })
export type EntradaDeClassificacaoDoRecebivel = z.infer<typeof entradaDeClassificacaoDoRecebivel>

/**
 * Reprogramar o FATURAMENTO de UMA parcela (0131) — e só isso.
 *
 * Um campo, de propósito. A agenda inteira se regrava pelo `PUT /pedidos/:id/recebiveis`,
 * que apaga e recria a partição; aqui a intenção é outra e menor: *"esta parcela vai ser
 * faturada noutro dia"*. Regravar a agenda para mover uma data trocaria os ids das
 * parcelas irmãs e derrubaria a que já foi faturada — por isso a operação existe.
 *
 * O que ela NÃO toca fica dito pela ausência: valor, vencimento, número da parcela e as
 * demais parcelas não estão aqui e não são alterados. Data passada é aceita: programação
 * vencida é um fato a mostrar, não um erro a recusar.
 */
export const entradaDeProgramacaoDeFaturamento = z.object({ faturarEm: dataNominal })
export type EntradaDeProgramacaoDeFaturamento = z.infer<typeof entradaDeProgramacaoDeFaturamento>

/** O que o PATCH devolve: a parcela, com a programação já gravada. */
export const programacaoDeFaturamento = z.object({
  id: uuid,
  numeroParcela: z.number().int(),
  faturarEm: dataNominal,
  vencimento: dataNominal,
  valor: z.string(),
})
export type ProgramacaoDeFaturamento = z.infer<typeof programacaoDeFaturamento>

export const itemContabilParaRecebivel = z.object({ id: uuid, nome: z.string() })
export type ItemContabilParaRecebivel = z.infer<typeof itemContabilParaRecebivel>

export const entradaDeLiquidacao = z.object({
  data: dataNominal,
  /** O que ENTROU de verdade — o fato bancário (D-91). */
  valor: z.string(),
  contaBancariaId: uuid,
  forma: z.string().nullable().optional(),
  diferencaTipo: tipoDeDiferenca.nullable().optional(),
  diferencaValor: z.string().nullable().optional(),
  diferencaMotivo: z.string().nullable().optional(),
  diferencaOrigem: origemDaDiferenca.nullable().optional(),
  /** Idempotência (D1): a mesma chave com o mesmo corpo devolve a MESMA liquidação. */
  chaveDeComando: chaveDeComando.optional(),
})
export type EntradaDeLiquidacao = z.infer<typeof entradaDeLiquidacao>

export const entradaDeContatoDeCobranca = z.object({
  /** Estável entre retries da mesma conversa por parcela; opcional para clientes antigos. */
  chaveDeRegistro: chaveDeComando.optional(),
  comQuemContatoId: uuid.nullable().optional(),
  comQuemNome: z.string().nullable().optional(),
  canal: canalDeCobranca,
  quando: z.string(),
  resultado: z.string().min(1),
  observacao: z.string().nullable().optional(),
  proximaAcao: z.string().nullable().optional(),
  proximaAcaoEm: dataNominal.nullable().optional(),
})
export type EntradaDeContatoDeCobranca = z.infer<typeof entradaDeContatoDeCobranca>

export const passoDaRegua = z.object({
  id: uuid.optional(),
  offsetDias: z.number().int(),
  canal: z.enum(['whatsapp', 'email', 'ambos']),
  responsavelId: uuid.nullable(),
  ordem: z.number().int(),
  ativo: z.boolean(),
})
export type PassoDaRegua = z.infer<typeof passoDaRegua>

export const entradaDePerdaEstimada = z.object({
  percentual: z.string(),
  motivo: z.string().min(1),
})

export const entradaDeSuspensaoDeRegua = z.object({ motivo: z.string().min(1) })

/** Os cinco cartões, sempre os cinco — inclusive os zerados (spec 4.4). */
export const totalDeAging = z.object({
  faixa: faixaDeAging,
  parcelas: z.number().int(),
  total: z.string(),
})
export type TotalDeAging = z.infer<typeof totalDeAging>

/** Uma linha do histórico de cobrança, como o drawer da 4.4 a mostra. */
export const contatoDeCobranca = z.object({
  id: uuid,
  canal: canalDeCobranca,
  quando: z.string(),
  resultado: z.string(),
  observacao: z.string().nullable(),
  proximaAcao: z.string().nullable(),
  proximaAcaoEm: dataNominal.nullable(),
  /** `regua` não tem autor: foi disparo automático, e a trilha não inventa um. */
  origem: z.enum(['usuario', 'regua']),
  comQuem: z.string().nullable(),
  /**
   * O contato com quem se falou, para o nome abrir a ficha dele (D-136, DS-538). Nulo no
   * nome digitado ("Outro") e no contato excluído depois: o nome fica, o link não.
   */
  comQuemContatoId: uuid.nullable(),
  registradoPor: z.string().nullable(),
  /** Ausente em resposta antiga equivale a registro ainda não desfeito. */
  revertidoEm: z.string().nullable().optional(),
  revertidoPor: z.string().nullable().optional(),
})
export type ContatoDeCobranca = z.infer<typeof contatoDeCobranca>


/** D98/D100: encargos da geração vigente, cobrados separadamente do principal fiscal. */
export interface EncargosDoRecebivel { encargosPendentes:string; podeReceber:boolean }

/** A capacidade reflete o ator e as dependências atuais, inclusive no replay. */
export interface BaixaRegistrada { id: string; capacidades: { podeEstornar: boolean } }
